import { 
  UserPlus, 
  Search, 
  Tractor, 
  Satellite, 
  MessageCircle, 
  Star, 
  Wallet, 
  Bell,
  Shield,
  MapPin
} from 'lucide-react';

const features = [
  {
    icon: UserPlus,
    title: 'Registro y Perfiles',
    description: 'Crea tu cuenta en segundos con correo, teléfono o redes sociales. Elige perfil Productor o Prestador y añade datos profesionales.',
    size: 'large',
    bullets: ['Verificación de identidad', 'Galería de trabajos', 'Documentos habilitantes']
  },
  {
    icon: Search,
    title: 'Búsqueda Avanzada',
    description: 'Encuentra servicios por ubicación, disponibilidad, precio y calificación.',
    size: 'medium',
    bullets: ['Filtros múltiples', 'Resultados en tiempo real']
  },
  {
    icon: Tractor,
    title: 'Rubros de Servicio',
    description: 'Amplio catálogo de servicios agropecuarios disponibles.',
    size: 'medium',
    bullets: ['Siembra', 'Fumigación', 'Cosecha', 'Transporte']
  },
  {
    icon: Satellite,
    title: 'Herramienta Satelital',
    description: 'Visualiza tus campos en mapas satelitales y planifica los trabajos con precisión georreferenciada.',
    size: 'large',
    bullets: ['Mapas en tiempo real', 'Delimitación de lotes', 'Historial de trabajos']
  },
  {
    icon: MessageCircle,
    title: 'Chat y Comunicación',
    description: 'Comunícate directamente con prestadores mediante chat integrado o llamadas.',
    size: 'small',
    bullets: ['Chat en tiempo real', 'Llamadas VoIP']
  },
  {
    icon: Star,
    title: 'Reseñas y Calificaciones',
    description: 'Sistema de reputación transparente basado en experiencias reales.',
    size: 'small',
    bullets: ['Calificaciones verificadas', 'Comentarios detallados']
  },
  {
    icon: Wallet,
    title: 'Pagos Integrados',
    description: 'Múltiples métodos de pago con garantía de cobro para prestadores.',
    size: 'medium',
    bullets: ['Transferencias', 'Billeteras digitales', 'Financiamiento en cuotas']
  },
  {
    icon: Bell,
    title: 'Notificaciones',
    description: 'Mantente informado sobre solicitudes, pagos y actualizaciones.',
    size: 'small',
    bullets: ['Push notifications', 'Email', 'SMS']
  }
];

export const FeaturesSection = () => {
  return (
    <section
      id="caracteristicas"
      className="py-16 md:py-24 bg-[#EBE9BD]"
      data-testid="features-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-bold uppercase tracking-[0.2em] text-[#AB6D4C] mb-4">
            Funcionalidades
          </span>
          <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#051914] mb-6">
            Lo que puedes hacer con Prestagro
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-[#051914]/70 max-w-3xl mx-auto">
            Una plataforma completa con todas las herramientas que necesitas para 
            gestionar servicios agropecuarios de forma eficiente.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Large Card - Registro */}
          <div 
            className="md:col-span-7 bg-[#FBF5EA] rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all border border-[#051914]/5"
            data-testid="feature-registro"
          >
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 bg-[#A9CB68] rounded-2xl flex items-center justify-center flex-shrink-0">
                <UserPlus size={28} className="text-[#051914]" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-medium text-[#051914] mb-3">
                  Registro y Perfiles
                </h3>
                <p className="text-[#051914]/70 mb-4">
                  Crea tu cuenta en segundos con correo, teléfono o redes sociales. 
                  Elige perfil Productor o Prestador y añade datos profesionales, 
                  galería de trabajos y verifica tu identidad.
                </p>
                <ul className="space-y-2">
                  {['Verificación de identidad', 'Galería de trabajos', 'Documentos habilitantes'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#051914]/60">
                      <Shield size={16} className="text-[#A9CB68]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Medium Card - Búsqueda */}
          <div 
            className="md:col-span-5 bg-[#051914] rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all"
            data-testid="feature-busqueda"
          >
            <div className="w-14 h-14 bg-[#A9CB68] rounded-2xl flex items-center justify-center mb-6">
              <Search size={28} className="text-[#051914]" />
            </div>
            <h3 className="text-xl md:text-2xl font-medium text-[#FBF5EA] mb-3">
              Búsqueda Avanzada
            </h3>
            <p className="text-[#FBF5EA]/70 mb-4">
              Encuentra servicios por ubicación, disponibilidad, precio y calificación 
              con filtros múltiples y resultados en tiempo real.
            </p>
          </div>

          {/* Medium Card - Satelital */}
          <div 
            className="md:col-span-5 bg-[#FBF5EA] rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all border border-[#051914]/5"
            data-testid="feature-satelital"
          >
            <div className="w-14 h-14 bg-[#A9CB68] rounded-2xl flex items-center justify-center mb-6">
              <Satellite size={28} className="text-[#051914]" />
            </div>
            <h3 className="text-xl md:text-2xl font-medium text-[#051914] mb-3">
              Herramienta Satelital
            </h3>
            <p className="text-[#051914]/70 mb-4">
              Visualiza tus campos en mapas satelitales y planifica trabajos con 
              precisión georreferenciada.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {['Mapas en tiempo real', 'Delimitación de lotes'].map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-[#EBE9BD] rounded-full text-xs text-[#051914]/70">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Large Card - Rubros */}
          <div 
            className="md:col-span-7 bg-[#A9CB68] rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all"
            data-testid="feature-rubros"
          >
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 bg-[#051914] rounded-2xl flex items-center justify-center flex-shrink-0">
                <Tractor size={28} className="text-[#A9CB68]" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-medium text-[#051914] mb-3">
                  Rubros de Servicio
                </h3>
                <p className="text-[#051914]/80 mb-4">
                  Amplio catálogo de servicios agropecuarios: siembra, fumigación, cosecha, transporte y más.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Siembra', 'Fumigación', 'Cosecha', 'Transporte', 'Laboreo', 'Drones'].map((rubro, i) => (
                    <span key={i} className="px-4 py-2 bg-[#051914] text-[#FBF5EA] rounded-full text-sm font-medium">
                      {rubro}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Small Cards Row */}
          <div 
            className="md:col-span-4 bg-[#FBF5EA] rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all border border-[#051914]/5"
            data-testid="feature-chat"
          >
            <div className="w-12 h-12 bg-[#A9CB68] rounded-xl flex items-center justify-center mb-4">
              <MessageCircle size={24} className="text-[#051914]" />
            </div>
            <h3 className="text-lg font-medium text-[#051914] mb-2">Chat y Comunicación</h3>
            <p className="text-sm text-[#051914]/60">Chat integrado y llamadas VoIP directas.</p>
          </div>

          <div 
            className="md:col-span-4 bg-[#FBF5EA] rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all border border-[#051914]/5"
            data-testid="feature-resenas"
          >
            <div className="w-12 h-12 bg-[#A9CB68] rounded-xl flex items-center justify-center mb-4">
              <Star size={24} className="text-[#051914]" />
            </div>
            <h3 className="text-lg font-medium text-[#051914] mb-2">Reseñas y Calificaciones</h3>
            <p className="text-sm text-[#051914]/60">Sistema de reputación transparente y verificado.</p>
          </div>

          <div 
            className="md:col-span-4 bg-[#FBF5EA] rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all border border-[#051914]/5"
            data-testid="feature-notificaciones"
          >
            <div className="w-12 h-12 bg-[#A9CB68] rounded-xl flex items-center justify-center mb-4">
              <Bell size={24} className="text-[#051914]" />
            </div>
            <h3 className="text-lg font-medium text-[#051914] mb-2">Notificaciones</h3>
            <p className="text-sm text-[#051914]/60">Push, email y SMS para mantenerte informado.</p>
          </div>

          {/* Full Width - Pagos */}
          <div 
            className="md:col-span-12 bg-[#051914] rounded-3xl p-8 md:p-10 hover:shadow-xl transition-all"
            data-testid="feature-pagos"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div className="w-16 h-16 bg-[#A9CB68] rounded-2xl flex items-center justify-center flex-shrink-0">
                <Wallet size={32} className="text-[#051914]" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl md:text-3xl font-medium text-[#FBF5EA] mb-3">
                  Pagos Integrados
                </h3>
                <p className="text-[#FBF5EA]/70 mb-4 max-w-2xl">
                  Múltiples métodos de pago con garantía de cobro para prestadores. 
                  Los productores pueden financiar el trabajo en cuotas mientras los 
                  proveedores cobran al instante con garantía. Bancos aliados ofrecen créditos integrados.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {['Transferencias', 'Billeteras digitales', 'Cuotas sin interés'].map((method, i) => (
                  <span key={i} className="px-4 py-2 border border-[#FBF5EA]/20 text-[#FBF5EA] rounded-full text-sm">
                    {method}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
